/**
 * Motor de conversão para a tela de Operador (modo manual).
 *
 * Replica as fórmulas de `fnCalculaSelos()` do dash legado e a base
 * líquida/bruta confirmada no plano — a legacy-api NÃO recalcula quando
 * `regraCartao.integrado !== true` (caso típico do Operador).
 *
 * Selos e regra-por-produto NÃO passam por aqui: usam
 * `POST /transaction/simulate/:action` na API real.
 */

export interface ReguaFaixa {
  valorMin: number;
  valorMax: number;
  quantidade: number;
}

export interface LoyaltyRegraCartao {
  status?: boolean;
  tipo?: string;
  regra?: number;
  multiplicador?: number;
  integrado?: boolean;
  validaValor?: boolean;
  valor?: number;
  valorResgate?: number;
  validaValorResgate?: boolean;
  cashLimit?: number;
  regua?: boolean;
  regraRegua?: ReguaFaixa[];
  pratileira?: boolean;
  oneOrderPerDay?: boolean;
  resgateNaoPontua?: boolean;
  arredondar?: boolean;
  inteiro?: boolean;
  hasWeek?: boolean;
  week?: number;
  data_inicio?: string;
  data_fim?: string;
  hasProductsByAdditional?: boolean;
  additionalInPercentage?: number;
  fixedRangeCashback?: boolean;
  fixedValue?: number;
  quantidade?: number;
}

export interface LoyaltyPassbook {
  _id?: string;
  hasPoints?: boolean;
  hasCashback?: boolean;
  hasTransaction?: boolean;
  hasGiftcard?: boolean;
  roleStamps?: number;
  maxInputValidation?: number;
  disableQtde?: boolean;
  regraCartao?: LoyaltyRegraCartao;
}

export type EarnBaseMode = 'net' | 'gross' | 'none';

export interface PreviewInput {
  passbook: LoyaltyPassbook;
  purchaseValue: number;
  /** Valor monetário (cashback/saldo) ou pontos a resgatar nesta conta. */
  redemptionAmount?: number;
  /**
   * Desconto em R$ na conta (ex.: produto do catálogo com redemptionType=discount).
   * Independente de `redemptionAmount` (que em pontos é custo em pts).
   */
  billDiscount?: number;
  /** true quando há resgate por produto/catálogo (não desconta da base). */
  productRedemption?: boolean;
  /** Saldo atual do cliente — usado no teto cashLimit. */
  balance?: number;
  /** Agora (injetável nos testes). */
  now?: Date;
}

export interface PreviewResult {
  program: 'cashback' | 'points' | 'stamps' | 'balance' | 'giftcard';
  purchaseValue: number;
  discount: number;
  netValue: number;
  earnBase: number;
  earnBaseMode: EarnBaseMode;
  pointsEarned: number;
  cashbackEarned: number;
  stampsEarned: number;
  /** Unidades a enviar em addPoints / send (passthrough). */
  awardUnits: number;
  message: string;
  resgateNaoPontua: boolean;
}

function trunc2(n: number): number {
  return Math.floor(n * 100 + 0.00001) / 100;
}

function cashbackPct(value: number, pct: number): number {
  return trunc2((value * pct) / 100);
}

function matchRegua(value: number, faixas: ReguaFaixa[] | undefined, inclusiveMax: boolean): ReguaFaixa | undefined {
  if (!faixas?.length) return undefined;
  for (const f of faixas) {
    const maxOk = inclusiveMax ? value <= f.valorMax : value < f.valorMax;
    if (value >= f.valorMin && maxOk) return f;
  }
  return undefined;
}

function formatYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function inDateWindow(now: Date, rc: LoyaltyRegraCartao): boolean {
  if (!rc.data_inicio || !rc.data_fim) return Boolean(rc.multiplicador && rc.multiplicador > 0 && !rc.data_inicio);
  const today = formatYmd(now);
  const start = rc.data_inicio.slice(0, 10);
  const end = rc.data_fim.slice(0, 10);
  return today >= start && today <= end;
}

/** Qual base usar para o novo ganho quando há resgate na mesma conta. */
export function resolveEarnBaseMode(passbook: LoyaltyPassbook, productRedemption = false): EarnBaseMode {
  if (passbook.hasTransaction || passbook.hasGiftcard) return 'none';
  if (!passbook.hasPoints && !passbook.hasCashback) return 'none'; // selo
  // Catálogo tipo "produto/brinde" (sem desconto em R$): ganha sobre o bruto.
  // Voucher com desconto em R$ força líquido em previewOperatorBill (discount > 0).
  if (productRedemption || passbook.regraCartao?.tipo === 'regra-por-produto' || passbook.regraCartao?.tipo === 'regra-de-produto' || passbook.regraCartao?.pratileira) {
    return 'gross';
  }
  if (passbook.hasCashback || passbook.hasPoints) return 'net';
  return 'none';
}

export function detectProgram(passbook: LoyaltyPassbook): PreviewResult['program'] {
  if (passbook.hasGiftcard) return 'giftcard';
  if (passbook.hasTransaction) return 'balance';
  if (passbook.hasCashback) return 'cashback';
  if (passbook.hasPoints) return 'points';
  return 'stamps';
}

/**
 * Converte valor de compra → unidades de fidelidade (espelha fnCalculaSelos).
 * Usado para pontos e cashback no modo operador (manual).
 */
export function computeAwardFromPurchase(
  passbook: LoyaltyPassbook,
  purchaseValue: number,
  now: Date = new Date(),
): number {
  const value = Number(purchaseValue) || 0;
  if (value <= 0) return 0;

  const rc = passbook.regraCartao;
  const regra = Number(rc?.regra) || 0;

  // Cashback (%)
  if (passbook.hasCashback) {
    if (rc?.validaValor && typeof rc.valor === 'number' && value < rc.valor) {
      // Régua ainda pode aplicar abaixo do mínimo em alguns clientes; front zera se validaValor.
      if (!rc.regua && !rc.regraRegua?.length) return 0;
    }

    if (rc?.fixedRangeCashback && rc.valor && rc.fixedValue != null) {
      const qtde = Math.trunc(value / rc.valor);
      return (rc.fixedValue || 0) * qtde;
    }

    let pct = regra;
    const faixa = matchRegua(value, rc?.regraRegua, true);
    if (faixa) pct = faixa.quantidade;

    let earned = cashbackPct(value, pct);

    if (rc?.hasWeek && typeof rc.week === 'number' && now.getDay() === rc.week && rc.multiplicador) {
      earned = earned * rc.multiplicador;
    }

    return earned;
  }

  // Pontos numéricos
  if (passbook.hasPoints) {
    if (rc?.validaValor && typeof rc.valor === 'number' && value < rc.valor) return 0;
    if (!regra || regra <= 0) return 0;

    const raw = value / regra;
    if (rc?.arredondar) return Math.ceil(raw);
    if (rc?.inteiro) return Math.trunc(Math.trunc(value) / regra);
    return Math.trunc(raw);
  }

  // Selos (só se alguém chamar o engine por engano — preferir simulate)
  if (passbook.roleStamps && passbook.roleStamps > 0) {
    return Math.floor(value / passbook.roleStamps);
  }

  const tipo = rc?.tipo;
  if (tipo === 'regra-de-valor' || tipo === 'regra-por-valor') {
    if (!regra || regra <= 0) return 0;
    let stamps = Math.floor(value / regra);
    const mult = Number(rc?.multiplicador) || 0;
    if (mult > 0 && inDateWindow(now, rc ?? {})) stamps *= Math.trunc(mult);
    return stamps;
  }

  // Legado/produção: `regra-fixa` (não "taxa"). Alias `regra-taxa` só por defesa.
  if (tipo === 'regra-fixa' || tipo === 'regra-por-valor-fixo' || tipo === 'regra-taxa') {
    if (rc?.validaValor && typeof rc.valor === 'number') {
      if (value < rc.valor) return 0;
      return Number(rc.quantidade) || 0;
    }
    const faixa = matchRegua(Math.floor(value), rc?.regraRegua, false);
    if (faixa) return faixa.quantidade;
    return Number(rc?.quantidade) || 0;
  }

  return 0;
}

/**
 * Teto de resgate utilizável.
 * Backend (user.js ~3624/3971) NÃO aplica cashLimit — só repassa a parceiros.
 * Enforcement é nosso: min(saldo, saldo*cashLimit/100, [compra − R$0,01 se desconto em R$]).
 * Default cashLimit = 100.
 * Piso de R$ 0,01 na conta: API/legado não aceitam compra zerada por cashback total.
 */
export const MIN_BILL_REMAINDER = 0.01;

export function redemptionCap(opts: {
  balance: number;
  cashLimit?: number | null;
  purchase?: number;
  program: PreviewResult['program'];
}): number {
  const balance = Math.max(0, Number(opts.balance) || 0);
  const rawLimit = opts.cashLimit;
  const pct =
    rawLimit == null || !Number.isFinite(Number(rawLimit))
      ? 100
      : Math.min(100, Math.max(0, Number(rawLimit)));
  const fromBalance = trunc2((balance * pct) / 100);
  const program = opts.program;
  if (program === 'cashback' || program === 'balance' || program === 'giftcard') {
    const purchase = Math.max(0, Number(opts.purchase) || 0);
    if (purchase > 0) {
      const maxAgainstPurchase = Math.max(0, trunc2(purchase - MIN_BILL_REMAINDER));
      return Math.min(fromBalance, maxAgainstPurchase);
    }
  }
  return fromBalance;
}

export function previewOperatorBill(input: PreviewInput): PreviewResult {
  const { passbook, purchaseValue, productRedemption = false, now = new Date() } = input;
  const purchase = Math.max(0, Number(purchaseValue) || 0);
  const program = detectProgram(passbook);
  const rc = passbook.regraCartao;

  let redemption = Math.max(0, Number(input.redemptionAmount) || 0);
  if (input.balance != null && Number.isFinite(Number(input.balance))) {
    const cap = redemptionCap({
      balance: Number(input.balance),
      cashLimit: rc?.cashLimit,
      purchase,
      program,
    });
    if (redemption > cap) redemption = cap;
  }

  const resgateNaoPontua = Boolean(rc?.resgateNaoPontua && redemption > 0);

  const earnBaseMode = resolveEarnBaseMode(passbook, productRedemption);

  // Desconto em R$ na conta:
  // - cashback/saldo/giftcard sem catálogo: redemptionAmount é R$
  // - catálogo tipo discount: billDiscount (price)
  // - catálogo tipo produto/brinde: NÃO usa custo em pts como R$
  const cashLike =
    program === 'cashback' || program === 'balance' || program === 'giftcard';
  const rawBill =
    input.billDiscount != null && Number.isFinite(Number(input.billDiscount))
      ? Math.max(0, Number(input.billDiscount))
      : cashLike && !productRedemption
        ? redemption
        : 0;
  // Compra 0 (só resgate giftcard/saldo): mostra o valor resgatado.
  // Com compra: nunca zera a conta (deixa MIN_BILL_REMAINDER) — nem cashback total, nem voucher.
  let discount: number;
  let netValue: number;
  if (purchase <= 0) {
    discount = rawBill;
    netValue = 0;
  } else {
    const maxDiscount = Math.max(0, trunc2(purchase - MIN_BILL_REMAINDER));
    discount = Math.min(rawBill, maxDiscount);
    // Cap atingido: líquido = piso (evita float 250−249.99 → trunc 0 → UI R$ 0,00).
    if (discount > 0 && discount >= maxDiscount) {
      netValue = MIN_BILL_REMAINDER;
    } else {
      netValue = Math.max(0, trunc2(purchase - discount));
    }
  }

  // Há desconto em R$ → ganho sempre sobre o líquido (valor pago).
  // Brinde/catálogo sem abatimento em R$ pode continuar no bruto (pratileira).
  let effectiveMode: EarnBaseMode = earnBaseMode;
  if (earnBaseMode !== 'none' && discount > 0) effectiveMode = 'net';

  let earnBase = purchase;
  if (effectiveMode === 'net') earnBase = netValue;
  if (effectiveMode === 'gross') earnBase = purchase;
  if (effectiveMode === 'none') earnBase = 0;

  let awardUnits = 0;
  if (!resgateNaoPontua && effectiveMode !== 'none') {
    awardUnits = computeAwardFromPurchase(passbook, earnBase, now);
  }

  const cashbackEarned = program === 'cashback' ? awardUnits : 0;
  const pointsEarned = program === 'points' ? awardUnits : 0;
  const stampsEarned = program === 'stamps' ? awardUnits : 0;

  const label =
    program === 'cashback'
      ? `R$ ${cashbackEarned.toFixed(2).replace('.', ',')}`
      : program === 'points'
        ? `${pointsEarned} ponto${pointsEarned === 1 ? '' : 's'}`
        : program === 'stamps'
          ? `${stampsEarned} selo${stampsEarned === 1 ? '' : 's'}`
          : '—';

  const message =
    awardUnits > 0
      ? `Compra: R$ ${purchase.toFixed(2)} · Cliente ganha: ${label}`
      : `Compra: R$ ${purchase.toFixed(2)} · Sem crédito de fidelidade nesta conta`;

  return {
    program,
    purchaseValue: purchase,
    discount,
    netValue,
    earnBase,
    earnBaseMode: effectiveMode,
    pointsEarned,
    cashbackEarned,
    stampsEarned,
    awardUnits,
    message,
    resgateNaoPontua,
  };
}
